import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import type { PropsWithChildren } from 'react';
import type { ParsedUrlQuery } from 'querystring';
import isEqual from 'lodash/isEqual';

import type { Geometry, GeometrySource, SearchQuery } from 'src/types/__generated__/graphql';
import type { AdSearchStateStore } from '../reducers/adSearchStateReducer';
import { adSearchStateReducer, ActionType, initialState } from '../reducers/adSearchStateReducer';
import { useManagedRouter } from 'src/hooks/useAppRouter';
import type { ExtendedAdsQueryVariables } from '../types/extendedAdsQueryVariables';
import { defaultListLimit, defaultQueryVariables } from 'src/constants/ad';
import { useAdLightLazyQuery, useSearchLazyQuery } from 'src/hooks/__generated__/queries';
import useTimeout from 'src/hooks/useTimeout';
import { routePathAd } from 'src/constants/router';
import type { ViewMode } from '../types/viewMode';
import { getAdsVariablesFromSavedSearch } from '../utils/getAdsVariablesFromSavedSearch';
import useUser from 'src/hooks/useUser';

type HandleChangeAdSearchStateParams = {
    adsQueryVariables?: Omit<ExtendedAdsQueryVariables, 'offset' | 'limit' | 'places' | 'place'> & {
        places?: ExtendedAdsQueryVariables['places'] | null;
        place?: ExtendedAdsQueryVariables['place'] | null;
    };
    isPolygonDisplayed?: boolean;
    viewMode?: ViewMode;
    fitMapOnAdSearchStateChange?: boolean;
    mapBounds?: Geometry | null;
    openSavedSearchTutorial?: boolean;
    isMapFullscreen?: boolean;
    isSavedSearchListModalOpen?: boolean;
};

type AdSearchContextValue = {
    adsQueryVariables: ExtendedAdsQueryVariables;
    isPolygonDisplayed: boolean;
    viewMode: ViewMode;
    isSavedSearchListModalOpen: boolean;
    savedSearchId: string | undefined;
    address: string | undefined;
    selectedSavedSearch: SearchQuery['search'] | undefined;
    fitMapOnAdSearchStateChange: boolean;
    isMapFullscreen: boolean;
    mapBounds: Geometry | undefined;
    openSavedSearchTutorial: boolean;
    hoveredAdData: AdSearchStateStore['hoveredAdState']['hoveredAdData'];
    setHoveredAd: (
        hoveredAd: AdSearchStateStore['hoveredAdState']['hoveredAd'],
        updateVisibleAdImmediately?: boolean,
    ) => void;
    changeAdSearchState: (params: HandleChangeAdSearchStateParams) => Promise<void>;
    changeMapFullscreen: (fullscreen: boolean) => void;
    loadSavedSearch: (searchId: string, openSavedSearchTutorial?: boolean) => Promise<void>;
    resetAdSearchState: () => Promise<void>;
};

const AdSearchStateContext = createContext<AdSearchContextValue>({} as AdSearchContextValue);

export const useAdSearchState = () => {
    const context = useContext(AdSearchStateContext);
    return context;
};

export const AdSearchStateProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const currentUser = useUser();
    const [adSearchStore, adSearchDispatcher] = useReducer(adSearchStateReducer, initialState);
    const [getSearch] = useSearchLazyQuery();

    useHoveredAdState({
        hoveredAdState: adSearchStore.hoveredAdState,
        setHoveredAdState(hoveredAdState) {
            adSearchDispatcher({ type: ActionType.SET_HOVERED_AD_STATE, payload: hoveredAdState });
        },
    });

    const {
        query,
        routerQueryState,
        changeAdsQueryVariables,
        changeViewMode,
        changeSavedSearchListModalOpen,
        changeMapBounds,
        replaceQuery,
    } = useRouterQuery();

    useEffect(() => {
        // Reset the state when the query is empty. It happens if the user clicks on Real Estate in the header or if the user is pushed on the page while he is already on it
        if (Object.keys(query).length === 0) {
            adSearchDispatcher({ type: ActionType.RESET });
        }
        // Load the saved search if the query contains the searchId
        if (query.searchId !== undefined && currentUser) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            loadSavedSearch(query.searchId as string);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, currentUser]);

    const handleSetHoveredAd = (
        hoveredAd: AdSearchStateStore['hoveredAdState']['hoveredAd'],
        updateVisibleAdImmediately?: boolean,
    ) => {
        adSearchDispatcher({
            type: ActionType.SET_HOVERED_AD_STATE,
            payload: {
                hoveredAd,
                ...(updateVisibleAdImmediately ? { visibleAd: hoveredAd } : {}),
            },
        });
    };

    const changeMapFullscreen = useCallback(
        (fullscreen: boolean) => {
            if (adSearchStore.isMapFullscreen !== fullscreen) {
                // I didn't find a way to do it in a better way with the library
                const fullscreenButton = document.querySelector<HTMLButtonElement>(
                    'button.gm-control-active.gm-fullscreen-control',
                );
                fullscreenButton?.click();
            }
        },
        [adSearchStore.isMapFullscreen],
    );

    const handleChangeAdSearchState = useCallback(
        async ({
            adsQueryVariables,
            isPolygonDisplayed,
            viewMode,
            isSavedSearchListModalOpen,
            fitMapOnAdSearchStateChange,
            mapBounds,
            openSavedSearchTutorial,
            isMapFullscreen,
        }: HandleChangeAdSearchStateParams) => {
            const { places, place, ...restAdsQueryVariables } = adsQueryVariables ?? {};

            let newQuery = { ...query };
            let queryChanged = false;
            if (Object.keys(restAdsQueryVariables).length > 0) {
                newQuery = changeAdsQueryVariables(restAdsQueryVariables, newQuery);
                queryChanged = true;
            }
            if (viewMode !== undefined) {
                newQuery = changeViewMode(viewMode, newQuery);
                queryChanged = true;
            }
            if (isSavedSearchListModalOpen !== undefined) {
                newQuery = changeSavedSearchListModalOpen(isSavedSearchListModalOpen, newQuery);
                queryChanged = true;
            }
            if (mapBounds !== undefined) {
                newQuery = changeMapBounds(mapBounds ?? undefined, newQuery);
                queryChanged = true;
            }

            if (queryChanged) {
                await replaceQuery(newQuery);
            }

            const needUpdateStore = [
                fitMapOnAdSearchStateChange,
                openSavedSearchTutorial,
                isMapFullscreen,
                places,
                place,
                isPolygonDisplayed,
            ].some((value) => value !== undefined);

            if (needUpdateStore) {
                adSearchDispatcher({
                    type: ActionType.SET_STORE_PARTIALLY,
                    payload: {
                        fitMapOnAdSearchStateChange,
                        openSavedSearchTutorial,
                        isMapFullscreen,
                        places,
                        place,
                        isPolygonDisplayed,
                    },
                });
            }
        },
        [changeAdsQueryVariables, changeMapBounds, changeSavedSearchListModalOpen, changeViewMode, query, replaceQuery],
    );

    const resetAdSearchState = useCallback(async () => {
        await replaceQuery({});
        adSearchDispatcher({ type: ActionType.RESET });
    }, [replaceQuery]);

    const loadSavedSearch = useCallback(
        async (searchId: string, openSavedSearchTutorial?: boolean) => {
            const search = await getSearch({ variables: { searchId } });
            const adsQueryVariables = getAdsVariablesFromSavedSearch(search.data?.search);

            const { places, place, ...restAdsQueryVariables } = adsQueryVariables ?? {};

            const newQuery = changeAdsQueryVariables(restAdsQueryVariables, {});
            await replaceQuery(newQuery);

            adSearchDispatcher({
                type: ActionType.LOAD_SAVED_SEARCH,
                payload: {
                    search: search.data?.search,
                    queryVariables: adsQueryVariables,
                    openSavedSearchTutorial,
                },
            });
        },
        [changeAdsQueryVariables, getSearch, replaceQuery],
    );

    const value = useMemo<AdSearchContextValue>(
        () => ({
            adsQueryVariables: {
                ...routerQueryState.adsQueryVariables,
                places: adSearchStore.places,
                place: adSearchStore.place,
            },
            viewMode: routerQueryState.viewMode,
            isSavedSearchListModalOpen: routerQueryState.isSavedSearchListModalOpen,
            savedSearchId: routerQueryState.searchId,
            address: routerQueryState.address,
            selectedSavedSearch: adSearchStore.selectedSavedSearch,
            fitMapOnAdSearchStateChange: adSearchStore.fitMapOnAdSearchStateChange,
            isMapFullscreen: adSearchStore.isMapFullscreen,
            isPolygonDisplayed: adSearchStore.isPolygonDisplayed,
            mapBounds: routerQueryState.mapBounds,
            openSavedSearchTutorial: adSearchStore.openSavedSearchTutorial,
            hoveredAdData: adSearchStore.hoveredAdState.hoveredAdData,
            setHoveredAd: handleSetHoveredAd,
            changeAdSearchState: handleChangeAdSearchState,
            changeMapFullscreen,
            loadSavedSearch,
            resetAdSearchState,
        }),
        [
            routerQueryState.adsQueryVariables,
            adSearchStore.places,
            adSearchStore.place,
            routerQueryState.viewMode,
            routerQueryState.isSavedSearchListModalOpen,
            routerQueryState.searchId,
            routerQueryState.address,
            routerQueryState.mapBounds,
            adSearchStore.selectedSavedSearch,
            adSearchStore.isPolygonDisplayed,
            adSearchStore.fitMapOnAdSearchStateChange,
            adSearchStore.isMapFullscreen,
            adSearchStore.openSavedSearchTutorial,
            adSearchStore.hoveredAdState.hoveredAdData,
            handleChangeAdSearchState,
            changeMapFullscreen,
            loadSavedSearch,
            resetAdSearchState,
        ],
    );

    return <AdSearchStateContext.Provider value={value}>{children}</AdSearchStateContext.Provider>;
};

type UseHoveredAdStateParams = {
    hoveredAdState: AdSearchStateStore['hoveredAdState'];
    setHoveredAdState: (hoveredAdState: AdSearchStateStore['hoveredAdState']) => void;
};
const useHoveredAdState = ({ hoveredAdState, setHoveredAdState }: UseHoveredAdStateParams) => {
    const [getAd] = useAdLightLazyQuery();

    // this timeout allows us to open the card only when user wait on the card 200ms
    useTimeout(
        () => {
            setHoveredAdState({
                visibleAd: hoveredAdState.hoveredAd,
            });
        },
        200,
        hoveredAdState.hoveredAd?.id,
    );

    const handleChangeHoveredAdData = useCallback(
        async (visibleAd: AdSearchStateStore['hoveredAdState']['visibleAd']) => {
            if (!visibleAd) {
                setHoveredAdState({
                    hoveredAdData: undefined,
                });
                return;
            }

            // createdAt is on AdLight query but not on ads for map. so it means that AdLight query is the one that has been used and we have all data needed to display the card
            const visibleAdHasEnoughData = Object.hasOwn(visibleAd, 'createdAt');

            if (visibleAdHasEnoughData) {
                setHoveredAdState({
                    hoveredAdData: visibleAd,
                });
            } else {
                const { data: adData } = await getAd({ variables: { id: visibleAd.id } });
                setHoveredAdState({
                    hoveredAdData: adData?.ad,
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getAd],
    );

    // This useEffect is used to handle when visibleAd is updated
    useEffect(() => {
        if (hoveredAdState.hoveredAd?.id === hoveredAdState.visibleAd?.id) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- we don't need to wait for the promise to resolve
            handleChangeHoveredAdData(hoveredAdState.visibleAd);
        }
    }, [handleChangeHoveredAdData, hoveredAdState.hoveredAd?.id, hoveredAdState.visibleAd]);
};

const useRouterQuery = () => {
    const { router } = useManagedRouter();

    const removeQueryParam = (query: ParsedUrlQuery, param: string) => {
        if (Object.keys(query).includes(param)) {
            const { [param]: deletedParam, ...newQuery } = query;
            return newQuery;
        }
        return query;
    };

    const addQueryParam = (query: ParsedUrlQuery, param: string, value: string) => {
        if (!Object.keys(query).includes(param)) {
            return {
                ...query,
                [param]: value,
            };
        }
        return query;
    };

    const replaceQueryParam = (
        query: ParsedUrlQuery,
        param: string,
        value: string,
        options?: { addIfNotExists?: boolean },
    ) => {
        if (Object.keys(query).includes(param)) {
            return {
                ...query,
                [param]: value,
            };
        }
        if (options?.addIfNotExists) {
            return addQueryParam(query, param, value);
        }
        return query;
    };

    const changeViewMode = (viewMode: ViewMode, originalQuery?: ParsedUrlQuery) => {
        const query = originalQuery ?? router.query;
        const newQuery = replaceQueryParam(query, 'viewMode', viewMode, { addIfNotExists: true });

        return newQuery;
    };

    const changeMapBounds = (mapBounds: Geometry | undefined, originalQuery?: ParsedUrlQuery) => {
        const query = originalQuery ?? router.query;
        let newQuery;
        if (mapBounds) {
            newQuery = replaceQueryParam(
                query,
                'mapBounds',
                encodeURIComponent(window.btoa(JSON.stringify(mapBounds))),
                {
                    addIfNotExists: true,
                },
            );
        } else {
            newQuery = removeQueryParam(query, 'mapBounds');
        }

        return newQuery;
    };

    const changeSavedSearchListModalOpen = (open: boolean, originalQuery?: ParsedUrlQuery) => {
        const query = originalQuery ?? router.query;
        let newQuery;
        if (open) {
            newQuery = replaceQueryParam(query, 'savedSearchListModalOpen', 'true', {
                addIfNotExists: true,
            });
        } else {
            newQuery = removeQueryParam(query, 'savedSearchListModalOpen');
        }

        return newQuery;
    };

    const changeAdsQueryVariables = (
        adsQueryVariables: Omit<ExtendedAdsQueryVariables, 'offset' | 'limit'>,
        originalQuery?: ParsedUrlQuery,
    ) => {
        const baseQuery = originalQuery ?? router.query;
        // We don't keep query param searchId because it's replaced by the params
        // We don't keep query param address because it's replaced by google place
        const { searchId, address, ...query } = baseQuery;

        if (adsQueryVariables.googlePlacesAutocompleteSearch) {
            query.googlePlacesAutocompleteSearch = adsQueryVariables.googlePlacesAutocompleteSearch;
        }
        if (adsQueryVariables.placesSource) {
            query.placesSource = adsQueryVariables.placesSource;
        }
        if (adsQueryVariables.orderBy) {
            query.orderBy = JSON.stringify(adsQueryVariables.orderBy);
        }
        if (adsQueryVariables.minPrice) {
            query.minPrice = String(adsQueryVariables.minPrice);
        }
        if (adsQueryVariables.maxPrice) {
            query.maxPrice = String(adsQueryVariables.maxPrice);
        }
        if (adsQueryVariables.minLandArea) {
            query.minLandArea = String(adsQueryVariables.minLandArea);
        }
        if (adsQueryVariables.maxLandArea) {
            query.maxLandArea = String(adsQueryVariables.maxLandArea);
        }
        if (adsQueryVariables.minLivingArea) {
            query.minLivingArea = String(adsQueryVariables.minLivingArea);
        }
        if (adsQueryVariables.maxLivingArea) {
            query.maxLivingArea = String(adsQueryVariables.maxLivingArea);
        }
        if (adsQueryVariables.rooms) {
            query.rooms = String(adsQueryVariables.rooms);
        }
        if (adsQueryVariables.bedrooms) {
            query.bedrooms = String(adsQueryVariables.bedrooms);
        }
        if (adsQueryVariables.house) {
            query.house = String(adsQueryVariables.house);
        }
        if (adsQueryVariables.apartment) {
            query.apartment = String(adsQueryVariables.apartment);
        }
        if (adsQueryVariables.loft) {
            query.loft = String(adsQueryVariables.loft);
        }
        if (adsQueryVariables.parking) {
            query.parking = String(adsQueryVariables.parking);
        }
        if (adsQueryVariables.terrain) {
            query.terrain = String(adsQueryVariables.terrain);
        }
        if (adsQueryVariables.newProperty) {
            query.newProperty = String(adsQueryVariables.newProperty);
        }
        if (adsQueryVariables.oldProperty) {
            query.oldProperty = String(adsQueryVariables.oldProperty);
        }
        if (adsQueryVariables.interOffice) {
            query.interOffice = String(adsQueryVariables.interOffice);
        }
        if (adsQueryVariables.furnished?.length) {
            query.furnished = JSON.stringify(adsQueryVariables.furnished);
        }
        if (adsQueryVariables.type) {
            query.type = adsQueryVariables.type;
        }
        if (adsQueryVariables.assets) {
            query.assets = JSON.stringify(adsQueryVariables.assets);
        }
        if (adsQueryVariables.isPrivateAdvertiser) {
            query.isPrivateAdvertiser = String(adsQueryVariables.isPrivateAdvertiser);
        }
        if (adsQueryVariables.realEstateTradesAdvertiser) {
            query.realEstateTradesAdvertiser = String(adsQueryVariables.realEstateTradesAdvertiser);
        }

        return query;
    };

    const replaceQuery = async (newQuery: ParsedUrlQuery) => {
        await router.replace({ pathname: routePathAd, query: newQuery }, undefined, {
            shallow: true,
        });
    };

    const routerQueryState = useMemo(() => {
        const query = router.query;

        const adsQueryVariables: ExtendedAdsQueryVariables = { ...defaultQueryVariables };
        if (query.googlePlacesAutocompleteSearch) {
            adsQueryVariables.googlePlacesAutocompleteSearch = query.googlePlacesAutocompleteSearch as string;
        }
        if (query.placesSource) {
            adsQueryVariables.placesSource = query.placesSource as GeometrySource;
        }
        if (query.orderBy) {
            adsQueryVariables.orderBy = JSON.parse(query.orderBy as string) as ExtendedAdsQueryVariables['orderBy'];
        }
        if (query.minPrice) {
            adsQueryVariables.minPrice = parseInt(query.minPrice as string, 10);
        }
        if (query.maxPrice) {
            adsQueryVariables.maxPrice = parseInt(query.maxPrice as string, 10);
        }
        if (query.minLandArea) {
            adsQueryVariables.minLandArea = parseInt(query.minLandArea as string, 10);
        }
        if (query.maxLandArea) {
            adsQueryVariables.maxLandArea = parseInt(query.maxLandArea as string, 10);
        }
        if (query.minLivingArea) {
            adsQueryVariables.minLivingArea = parseInt(query.minLivingArea as string, 10);
        }
        if (query.maxLivingArea) {
            adsQueryVariables.maxLivingArea = parseInt(query.maxLivingArea as string, 10);
        }
        if (query.rooms) {
            adsQueryVariables.rooms = parseInt(query.rooms as string, 10);
        }
        if (query.bedrooms) {
            adsQueryVariables.bedrooms = parseInt(query.bedrooms as string, 10);
        }
        if (query.house) {
            adsQueryVariables.house = query.house === 'true';
        }
        if (query.apartment) {
            adsQueryVariables.apartment = query.apartment === 'true';
        }
        if (query.loft) {
            adsQueryVariables.loft = query.loft === 'true';
        }
        if (query.parking) {
            adsQueryVariables.parking = query.parking === 'true';
        }
        if (query.terrain) {
            adsQueryVariables.terrain = query.terrain === 'true';
        }
        if (query.newProperty) {
            adsQueryVariables.newProperty = query.newProperty === 'true';
        }
        if (query.oldProperty) {
            adsQueryVariables.oldProperty = query.oldProperty === 'true';
        }
        if (query.interOffice) {
            adsQueryVariables.interOffice = query.interOffice === 'true';
        }
        if (query.furnished) {
            adsQueryVariables.furnished = JSON.parse(
                query.furnished as string,
            ) as ExtendedAdsQueryVariables['furnished'];
        }
        if (query.type) {
            adsQueryVariables.type = query.type as ExtendedAdsQueryVariables['type'];
        }
        if (query.assets) {
            adsQueryVariables.assets = JSON.parse(query.assets as string) as ExtendedAdsQueryVariables['assets'];
        }
        if (query.isPrivateAdvertiser) {
            adsQueryVariables.isPrivateAdvertiser = query.isPrivateAdvertiser === 'true';
        }
        if (query.realEstateTradesAdvertiser) {
            adsQueryVariables.realEstateTradesAdvertiser = query.realEstateTradesAdvertiser === 'true';
        }
        if (query.page) {
            const pageAsNumber = parseInt(query.page as string, 10);

            if (!Number.isNaN(pageAsNumber) && pageAsNumber > 0) {
                adsQueryVariables.offset = (pageAsNumber - 1) * defaultListLimit;
            }
        }

        return {
            adsQueryVariables,
            searchId: query.searchId as string | undefined,
            viewMode: (query.viewMode === 'list' ? 'list' : 'map') as ViewMode,
            isSavedSearchListModalOpen: query.savedSearchListModalOpen === 'true',
            mapBounds: query.mapBounds
                ? (JSON.parse(window.atob(decodeURIComponent(query.mapBounds as string))) as Geometry)
                : undefined,
            address: query.address as string | undefined,
        };
    }, [router.query]);

    return {
        query: router.query,
        routerQueryState,
        replaceQuery,
        changeViewMode,
        changeSavedSearchListModalOpen,
        changeMapBounds,
        changeAdsQueryVariables,
    };
};
